import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit, UploadFile, Visibility } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { api, extractError } from '../../../shared/api';
import type { Class, Student } from '../../../shared/types';
import { useAuthStore } from '../../../features/auth/store';
import PageHeader from '../../../shared/PageHeader';
import { defaultStudentPassword, formatDate } from '../../../shared/utils';

export default function StudentListPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'ADMIN';
  const qc = useQueryClient();
  const [classFilter, setClassFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Student | null>(null);
  const [creating, setCreating] = useState(false);

  const classesQ = useQuery({ queryKey: ['classes'], queryFn: async () => (await api.get<Class[]>('/classes')).data });
  const studentsQ = useQuery({
    queryKey: ['students', classFilter, search],
    queryFn: async () => (await api.get<Student[]>('/admin/students', {
      params: { ...(classFilter && { classId: classFilter }), ...(search && { search }) },
    })).data,
  });
  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/admin/students/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['students'] }),
  });

  const classMap = new Map((classesQ.data ?? []).map((c) => [c.id, `${c.name}-${c.section}`]));

  return (
    <Box>
      <PageHeader
        title="Students"
        subtitle={`${studentsQ.data?.length ?? 0} enrolled across ${classesQ.data?.length ?? 0} classes`}
        action={isAdmin && (
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" startIcon={<UploadFile />} component={RouterLink} to="/portal/students/bulk">
              Bulk upload
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreating(true)}>Add student</Button>
          </Stack>
        )}
      />

      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select label="Class" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All classes</MenuItem>
            {(classesQ.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}-{c.section}</MenuItem>
            ))}
          </TextField>
          <TextField label="Search by name or roll" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1 }} />
        </Stack>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Roll</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>DOB</TableCell>
              <TableCell>Parent contact</TableCell>
              <TableCell>Default password</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {(studentsQ.data ?? []).map((s) => (
              <TableRow key={s.id} hover>
                <TableCell sx={{ fontWeight: 700 }}>{s.rollNumber}</TableCell>
                <TableCell>{s.firstName} {s.lastName}</TableCell>
                <TableCell><Chip size="small" label={classMap.get(s.classId) ?? '—'} /></TableCell>
                <TableCell>{formatDate(s.dob)}</TableCell>
                <TableCell><Box component="span" sx={{ display: 'block', fontSize: 12 }}>{s.parentName}<br /><Typography variant="caption" color="text.secondary">{s.parentPhone}</Typography></Box></TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#F1F5F9', px: 1, py: 0.5, borderRadius: 1 }}>
                    {s.defaultPassword ?? defaultStudentPassword(s.rollNumber, s.firstName, s.dob)}
                  </Typography>
                </TableCell>
                {isAdmin && (
                  <TableCell align="right">
                    <Tooltip title="View"><IconButton size="small"><Visibility /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(s)}><Edit /></IconButton></Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => { if (window.confirm(`Delete ${s.firstName}?`)) del.mutate(s.id); }}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {(studentsQ.data?.length ?? 0) === 0 && (
              <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No students match.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <StudentForm
        open={creating || !!editing}
        student={editing}
        classes={classesQ.data ?? []}
        onClose={() => { setCreating(false); setEditing(null); }}
      />
    </Box>
  );
}

function StudentForm({ open, student, classes, onClose }: { open: boolean; student: Student | null; classes: Class[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Partial<Student>>(student ?? { gender: 'MALE' });
  const isEdit = !!student;

  const set = (k: keyof Student, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        return (await api.put(`/admin/students/${student!.id}`, form)).data;
      }
      return (await api.post('/admin/students', form)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['students'] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" TransitionProps={{ onEntered: () => setForm(student ?? { gender: 'MALE' }) }}>
      <DialogTitle>{isEdit ? 'Edit student' : 'Enroll new student'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }} direction={{ md: 'row' }} flexWrap="wrap">
          <TextField label="Roll number" value={form.rollNumber ?? ''} onChange={(e) => set('rollNumber', e.target.value)} disabled={isEdit} required sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Admission number" value={form.admissionNumber ?? ''} onChange={(e) => set('admissionNumber', e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="First name" value={form.firstName ?? ''} onChange={(e) => set('firstName', e.target.value)} required sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Last name" value={form.lastName ?? ''} onChange={(e) => set('lastName', e.target.value)} required sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Date of Birth" type="date" InputLabelProps={{ shrink: true }} value={form.dob ?? ''} onChange={(e) => set('dob', e.target.value)} required sx={{ flex: 1, minWidth: 220 }} />
          <TextField select label="Gender" value={form.gender ?? 'MALE'} onChange={(e) => set('gender', e.target.value)} sx={{ flex: 1, minWidth: 220 }}>
            <MenuItem value="MALE">Male</MenuItem>
            <MenuItem value="FEMALE">Female</MenuItem>
            <MenuItem value="OTHER">Other</MenuItem>
          </TextField>
          <TextField select label="Class" value={form.classId ?? ''} onChange={(e) => set('classId', e.target.value)} required sx={{ flex: 1, minWidth: 220 }}>
            {classes.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}-{c.section}</MenuItem>)}
          </TextField>
          <TextField label="Blood group" value={form.bloodGroup ?? ''} onChange={(e) => set('bloodGroup', e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Parent name" value={form.parentName ?? ''} onChange={(e) => set('parentName', e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Parent phone" value={form.parentPhone ?? ''} onChange={(e) => set('parentPhone', e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Parent email" value={form.parentEmail ?? ''} onChange={(e) => set('parentEmail', e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
          {save.isError && <Alert severity="error">{extractError(save.error)}</Alert>}
        </Stack>
        {!isEdit && form.rollNumber && form.firstName && form.dob && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Default password will be: <b>{defaultStudentPassword(form.rollNumber, form.firstName, form.dob)}</b>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Enroll'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
